import type { OrdemDoBacklog, OrdemDosArtigos, RecorteDoBacklog } from '@titularis/api-client';

/**
 * Em que ordem as duas listas de artigos saem - o backlog e o inventário.
 *
 * **Num lugar só, e com o mesmo parâmetro na URL**, pelo mesmo motivo do filtro
 * de lei: as abas são a mesma sequência de trabalho, e escolher "Na ordem da
 * lei" para achar um artigo não pode se perder ao passar para a aba do lado. A
 * ordem que só uma das duas conhece cai no padrão da outra, que é o que o
 * `lerOpcao` faz.
 *
 * **A ordenação é do servidor, e não da tabela.** O inventário chega cortado
 * pela página, e ordenar depois do corte mostraria um pedaço arbitrário em ordem
 * bonita. O backlog chega inteiro e ainda assim ordena lá: são as mesmas duas
 * abas escrevendo o mesmo parâmetro, e uma ordem da tabela e outra do servidor
 * discordariam na primeira troca de aba. É por isso que o cabeçalho da coluna e
 * o seletor escrevem no mesmo lugar em vez de a tabela ordenar sozinha.
 */

/** A ordem em que a lei se lê, que é a que faltava nas duas listas. */
export const ORDEM_DA_LEI = 'artigo';

export const ORDENS: { value: OrdemDosArtigos; label: string }[] = [
  { value: 'recentes', label: 'Mexidos por último' },
  /*
   * O avesso, que é o que responde "o que ninguém toca há mais tempo" - a
   * pergunta que a ordem por recentes nunca responde, porque o esquecido é
   * justamente o que ela empurra para a última página.
   */
  { value: 'antigos', label: 'Parados há mais tempo' },
  /** A ordem do que mais importa: o comentário que mais aluno vai ler. */
  { value: 'incidencia', label: 'Mais cobrados na prova' },
  /**
   * A ordem de varrer, e não a de escolher.
   *
   * As outras três respondem "por onde começar"; esta responde "o que ainda
   * falta *aqui*" - a pergunta de quem está fechando um capítulo e precisa ver
   * os artigos dele em sequência, do menor para o maior, com o `1.245` depois do
   * `13` e não entre o `12` e ele.
   */
  { value: ORDEM_DA_LEI, label: 'Na ordem da lei' },
];

/**
 * As do backlog, que são duas.
 *
 * A incidência é o padrão e continua sendo: a aba existe para responder o que
 * comentar em seguida, e o que mais cai é a resposta.
 */
export const ORDENS_DO_BACKLOG: { value: OrdemDoBacklog; label: string }[] = [
  { value: 'incidencia', label: 'Mais cobrados na prova' },
  { value: ORDEM_DA_LEI, label: 'Na ordem da lei' },
];

/** A varredura da lei escolhida, que é o recorte que não é o padrão. */
export const VARREDURA_DA_LEI = 'toda-a-lei';

/**
 * De onde a lista sai, antes de qualquer ordem.
 *
 * **É um controle, e não uma consequência de escolher a lei.** No recorte de
 * sempre, filtrar por lei quer dizer "o que vale a pena comentar *nesta* lei", e
 * despejar ali os artigos que ninguém cobra tiraria da lista o que a torna útil.
 * A varredura responde outra pergunta - "comentar a lei toda" - e por isso pede
 * um clique próprio.
 *
 * O vocabulário é o da tela da lei (`Cobrados na prova`), porque é a mesma
 * distinção: dizer o mesmo de dois jeitos faria parecerem duas coisas.
 */
export const RECORTES_DO_BACKLOG: { value: RecorteDoBacklog; label: string }[] = [
  { value: 'cobrados', label: 'Cobrados na prova' },
  { value: VARREDURA_DA_LEI, label: 'Toda a lei' },
];

/**
 * A opção que veio da URL, se for uma que esta lista conhece.
 *
 * O que vem de lá é texto que qualquer um digita na barra de endereço - e, aqui,
 * texto que a aba vizinha pode ter escrito. Nos dois casos o desconhecido vira
 * `undefined`, que é o padrão de quem pergunta.
 *
 * Serve à ordem e ao recorte: são a mesma leitura, e duas cópias divergiriam no
 * dia em que uma delas aprendesse a lidar com o valor inventado.
 */
export function lerOpcao<T extends string>(
  bruta: string | undefined,
  conhecidas: { value: T }[],
): T | undefined {
  return conhecidas.some((opcao) => opcao.value === bruta) ? (bruta as T) : undefined;
}
